import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api/apiClient';
import { AxiosResponse } from 'axios';
import {
  InvestmentChecklistResponse,
  UpdateInvestmentChecklistAnchorsRequestBody,
  UpdateInvestmentChecklistTaskRequestBody,
  InvestmentChecklistTaskResponse,
  ChecklistPhase,
  GenerateNotaryEmailDraftRequestBody,
  GenerateNotaryEmailDraftResponse,
} from '@inversor-hub/types';
import { LIST_INVESTMENTS_KEY } from '@/lib/api/investments';

export const INVESTMENT_CHECKLIST_KEY = 'investment-checklist';

export function useGetInvestmentChecklist(investmentId?: string) {
  return useQuery({
    queryKey: [INVESTMENT_CHECKLIST_KEY, investmentId],
    queryFn: async () => {
      return apiClient.get<InvestmentChecklistResponse>(`/investments/${investmentId}/checklist`);
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

export function useGenerateNotaryEmailDraft(investmentId: string) {
  return useMutation({
    mutationFn: async (data: GenerateNotaryEmailDraftRequestBody) => {
      const response = await apiClient.post<
        GenerateNotaryEmailDraftResponse,
        AxiosResponse<GenerateNotaryEmailDraftResponse>,
        GenerateNotaryEmailDraftRequestBody
      >(`/investments/${investmentId}/checklist/notary-email/draft`, data);
      return response.data.data;
    },
  });
}

export function useUpdateChecklistAnchors(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateInvestmentChecklistAnchorsRequestBody) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<InvestmentChecklistResponse>,
        UpdateInvestmentChecklistAnchorsRequestBody
      >(`/investments/${investmentId}/checklist/anchors`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_CHECKLIST_KEY, investmentId] });
    },
  });
}

export function useUpdateChecklistTask(investmentId: string, taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateInvestmentChecklistTaskRequestBody) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<InvestmentChecklistTaskResponse>,
        UpdateInvestmentChecklistTaskRequestBody
      >(`/investments/${investmentId}/checklist/tasks/${taskId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_CHECKLIST_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
    },
  });
}

export function useUploadChecklistDocument(investmentId: string, taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<InvestmentChecklistTaskResponse>(
        `/investments/${investmentId}/checklist/tasks/${taskId}/documents`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_CHECKLIST_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
    },
  });
}

export function useDeleteChecklistDocument(investmentId: string, taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {
      const response = await apiClient.delete<InvestmentChecklistTaskResponse>(
        `/investments/${investmentId}/checklist/tasks/${taskId}/documents/${mediaId}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_CHECKLIST_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
    },
  });
}

export function useSkipChecklistPhase(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (phase: ChecklistPhase) => {
      const response = await apiClient.post<InvestmentChecklistResponse>(
        `/investments/${investmentId}/checklist/phases/${phase}/skip`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_CHECKLIST_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTMENTS_KEY] });
    },
  });
}
